import { Component, Host, h, Element, State, Prop } from '@stencil/core';

export interface A {
  currentIndex: number;
  currentColumns: number;
  rows: HTMLElement[][];
}

@Component({
  tag: 'ui-cms-grid',
  styleUrls: {
    default: 'ui-cms-grid.scss',
    editing: 'ui-cms-grid.magnolia.scss',
  },
})
export class UiCmsGrid {
  @Element() el: HTMLElement;

  @Prop() columns = 12;

  @Prop() rowGap = 64;

  @State() rows: HTMLUiCmsGridElementElement[][] = [];

  componentWillLoad() {
    this.el.style.setProperty('--grid-column-width', (1 / this.columns).toString());
    this.el.style.setProperty('--grid-row-gap', this.rowGap.toString());

    this.rows = this.createRows();

    this.rows?.forEach((row, index) => {
      // Set the row gap reduction to the grid elements. This will reduce the gap between the grid elements.
      row.forEach((element) => {
        element.style.setProperty('--grid-row-gap-reduction', this.rowGap.toString());
      });

      // Add separators between grid elements. If two elements are next to each other the separator will be added after the last element.
      if (row.every((element) => !element.hideSeparator && index !== this.rows.length - 1)) {
        row.at(-1).insertAdjacentHTML('afterend', '<hr />');
      }
    });
  }

  /**
   * Since the grid elements can have different column sizes, we need to calculate the rows based on the column sizes.
   * This function will create the rows based on the column sizes of the grid elements.
   */
  private createRows(): HTMLUiCmsGridElementElement[][] {
    const elements: HTMLUiCmsGridElementElement[] = Array.from(
      this.el.querySelectorAll(':scope > ui-cms-grid-element')
    );
    return elements.reduce(
      (accumulator, element) => {
        const shouldStartNewRow = this.shouldStartNewRow(accumulator, element);
        const newColumns = shouldStartNewRow ? element.columns : accumulator.currentColumns + element.columns;

        return {
          currentColumns: element.centered ? 12 : newColumns,
          currentIndex: shouldStartNewRow ? accumulator.currentIndex + 1 : accumulator.currentIndex,
          rows: shouldStartNewRow
            ? [...accumulator.rows, [element]]
            : accumulator.rows.map((row, index) => (index === accumulator.currentIndex ? [...row, element] : row)),
        };
      },
      {
        currentIndex: 0,
        currentColumns: 0,
        rows: [[]] as HTMLUiCmsGridElementElement[][],
      }
    ).rows;
  }

  private shouldStartNewRow(accumulator, element: HTMLUiCmsGridElementElement): boolean {
    return (
      accumulator.currentColumns + element.columns > this.columns ||
      (element.centered && accumulator.currentIndex !== 0)
    );
  }

  render() {
    return (
      <Host
        class={{
          isB2b: window.ui.cmsState.isB2b,
        }}
      >
        <slot></slot>
      </Host>
    );
  }
}
